// Shared API base URL resolution for every frontend module.
//
// Self-contained ON PURPOSE: POS modules that import this also run under
// Node's test runner (--experimental-strip-types), where extensionless
// imports and import.meta.env do not exist. Keep this file free of imports,
// and keep env access optional-chained.

function isLoopbackHost(hostname: string) {
  return hostname === "localhost"
    || hostname === "127.0.0.1"
    || hostname === "::1"
    || hostname.endsWith(".localhost");
}

function isIpHost(hostname: string) {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
    || hostname.includes(":");
}

export function getApiBaseUrl() {
  const configuredBaseUrl = (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim();
  if (typeof window !== "undefined") {
    const currentOrigin = window.location.origin;
    if (configuredBaseUrl) {
      try {
        const configuredUrl = new URL(configuredBaseUrl);
        const currentUrl = new URL(currentOrigin);
        const currentIsLoopback = isLoopbackHost(currentUrl.hostname);
        const configuredIsLoopback = isLoopbackHost(configuredUrl.hostname);

        // A loopback API base only makes sense when the app itself runs on
        // loopback (local dev). Served from a real host, it would send every
        // visitor's browser to their own machine — a build-time env mistake.
        if (configuredIsLoopback && !currentIsLoopback) {
          console.warn("[auth] VITE_API_BASE_URL points at loopback while the app is served from a real host; using same-origin API routing instead", {
            configuredOrigin: configuredUrl.origin,
            currentOrigin,
          });
          return currentOrigin;
        }

        if (isIpHost(currentUrl.hostname) && !isIpHost(configuredUrl.hostname)) {
          if (import.meta.env?.DEV) console.info("[auth-debug]", {
            area: "backend-auth",
            event: "prefer_configured_api_origin_for_ip_host",
            configuredOrigin: configuredUrl.origin,
            currentOrigin,
          });
          return configuredUrl.origin;
        }

        if (!currentIsLoopback && !configuredIsLoopback && configuredUrl.origin !== currentUrl.origin) {
          console.warn("[auth] configured API base origin differs from the active browser origin; using same-origin API routing instead", {
            configuredOrigin: configuredUrl.origin,
            currentOrigin,
          });
          return currentOrigin;
        }
      } catch (error) {
        console.warn("[auth] invalid VITE_API_BASE_URL; falling back to browser origin", {
          configuredBaseUrl,
          error: error instanceof Error ? error.message : String(error),
        });
        return currentOrigin;
      }

      return configuredBaseUrl.replace(/\/+$/, "");
    }

    return currentOrigin;
  }

  return configuredBaseUrl?.replace(/\/+$/, "") ?? "";
}
